import {
  clearPersistedSession,
  loadPersistedSession,
} from "../features/session/sessionRepository";

const DEFAULT_API_BASE_URL = "/api";
const API_BASE_URL = String(
  process.env.PARCEL_PUBLIC_API_BASE_URL || DEFAULT_API_BASE_URL
).replace(/\/$/, "");
const DEFAULT_AUTH_TOKEN = String(process.env.PARCEL_PUBLIC_API_AUTH_TOKEN || "").trim();
const FORCE_MOCK_MODE =
  String(process.env.PARCEL_PUBLIC_USE_MOCK_DATA || "").toLowerCase() === "true";
let authRedirectTriggered = false;

function toSearchParams(params = {}) {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") {
      return;
    }

    if (Array.isArray(value)) {
      value.forEach((entry) => searchParams.append(key, String(entry)));
      return;
    }

    searchParams.append(key, String(value));
  });

  return searchParams.toString();
}

function buildUrl(path, params) {
  const isAbsolute = /^https?:\/\//i.test(path);
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  const resolvedPath = isAbsolute
    ? path
    : normalizedPath.startsWith(`${API_BASE_URL}/`) || normalizedPath === API_BASE_URL
      ? normalizedPath
      : `${API_BASE_URL}${normalizedPath}`;

  const query = toSearchParams(params);
  return query ? `${resolvedPath}?${query}` : resolvedPath;
}

export function getApiBaseUrl() {
  return API_BASE_URL || DEFAULT_API_BASE_URL;
}

export function isMockModeForced() {
  return FORCE_MOCK_MODE;
}

export function getAccessToken() {
  const session = loadPersistedSession();
  const sessionToken = String(session?.accessToken || "").trim();

  return sessionToken || DEFAULT_AUTH_TOKEN || "";
}

function isExpiredTokenMessage(message) {
  return /token expired|jwt expired|expired jwt/i.test(String(message || ""));
}

function redirectToLogin() {
  if (authRedirectTriggered || typeof window === "undefined") {
    return;
  }

  authRedirectTriggered = true;
  clearPersistedSession();

  if (window.location.pathname === "/login") {
    window.location.replace("/login");
    return;
  }

  const redirectTo = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  const loginUrl = redirectTo && redirectTo !== "/"
    ? `/login?redirect=${encodeURIComponent(redirectTo)}`
    : "/login";
  window.location.replace(loginUrl);
}

function shouldRedirectForAuthFailure({ requiresAuth, status, message }) {
  if (!requiresAuth) {
    return false;
  }

  return Number(status) === 401 || isExpiredTokenMessage(message);
}

function getPayloadErrorMessage(payload, fallbackStatus, fallbackStatusText) {
  if (payload && typeof payload === "object") {
    return (
      payload.message ||
      payload.detail ||
      payload.error ||
      payload.title ||
      `Request failed: ${fallbackStatus} ${fallbackStatusText}`
    );
  }

  if (typeof payload === "string" && payload) {
    return payload;
  }

  return `Request failed: ${fallbackStatus} ${fallbackStatusText}`;
}

function parseContentDispositionFileName(contentDisposition) {
  const rawValue = String(contentDisposition || "").trim();

  if (!rawValue) {
    return "";
  }

  const utf8Match = rawValue.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1].replace(/["']/g, ""));
    } catch (error) {
      return utf8Match[1].replace(/["']/g, "");
    }
  }

  const standardMatch = rawValue.match(/filename\s*=\s*"([^"]+)"/i);
  if (standardMatch?.[1]) {
    return standardMatch[1];
  }

  const fallbackMatch = rawValue.match(/filename\s*=\s*([^;]+)/i);
  return fallbackMatch?.[1] ? fallbackMatch[1].replace(/["']/g, "").trim() : "";
}

export async function request(path, options = {}) {
  const {
    method ,
    params,
    body,
    headers = {},
    signal,
    requiresAuth = false,
  } = options;
  const url = buildUrl(path, params);
  const requestHeaders = {
    Accept: "application/json, text/plain;q=0.9, */*;q=0.8",
    ...headers,
  };
  const init = {
    method,
    headers: requestHeaders,
    signal,
  };

  if (body !== undefined) {
    if (body instanceof FormData) {
      init.body = body;
    } else {
      requestHeaders["Content-Type"] =
        requestHeaders["Content-Type"] || "application/json";
      init.body = JSON.stringify(body);
    }
  }

  if (requiresAuth) {
    const accessToken = getAccessToken();

    if (accessToken) {
      requestHeaders.Authorization = `Bearer ${accessToken}`;
    }
  }

  let response;

  try {
    response = await fetch(url, init);
  } catch (error) {
    const message =
      error?.name === "AbortError"
        ? "Request was cancelled."
        : `Could not reach ${url}.`;
    const wrappedError = new Error(message);
    wrappedError.cause = error;
    throw wrappedError;
  }

  if (response.status === 204) {
    return null;
  }

  const contentType = response.headers.get("content-type") || "";
  const payload = contentType.includes("application/json")
    ? await response.json()
    : await response.text();

  const payloadStatus =
    payload && typeof payload === "object" ? Number(payload.status) : NaN;
  const hasPayloadErrorStatus = Number.isFinite(payloadStatus) && payloadStatus >= 400;

  if (!response.ok || hasPayloadErrorStatus) {
    const message = getPayloadErrorMessage(
      payload,
      response.status || payloadStatus,
      response.statusText
    );
    const errorStatus = hasPayloadErrorStatus ? payloadStatus : response.status;

    if (
      shouldRedirectForAuthFailure({
        requiresAuth,
        status: errorStatus,
        message,
      })
    ) {
      redirectToLogin();
    }

    const error = new Error(message);
    error.status = errorStatus;
    error.payload = payload;
    throw error;
  }

  return payload;
}

export function getJson(path, options = {}) {
  return request(path, { ...options, method: "GET" });
}

export function postJson(path, body, options = {}) {
  return request(path, { ...options, method: "POST", body });
}

export function putJson(path, body, options = {}) {
  return request(path, { ...options, method: "PUT", body });
}

export function deleteJson(path, options = {}) {
  return request(path, { ...options, method: "DELETE" });
}

export async function downloadBinary(path, options = {}) {
  const {
    params,
    headers = {},
    signal,
    requiresAuth = false,
  } = options;
  const url = buildUrl(path, params);
  const requestHeaders = {
    Accept:
      "application/octet-stream, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/json, text/plain;q=0.9, */*;q=0.8",
    ...headers,
  };

  if (requiresAuth) {
    const accessToken = getAccessToken();

    if (accessToken) {
      requestHeaders.Authorization = `Bearer ${accessToken}`;
    }
  }

  let response;

  try {
    response = await fetch(url, {
      method: "GET",
      headers: requestHeaders,
      signal,
    });
  } catch (error) {
    const message =
      error?.name === "AbortError"
        ? "Request was cancelled."
        : `Could not reach ${url}.`;
    const wrappedError = new Error(message);
    wrappedError.cause = error;
    throw wrappedError;
  }

  if (!response.ok) {
    const contentType = response.headers.get("content-type") || "";
    const payload = contentType.includes("application/json")
      ? await response.json()
      : await response.text();
    const message = getPayloadErrorMessage(payload, response.status, response.statusText);

    if (
      shouldRedirectForAuthFailure({
        requiresAuth,
        status: response.status,
        message,
      })
    ) {
      redirectToLogin();
    }

    const error = new Error(message);
    error.status = response.status;
    error.payload = payload;
    throw error;
  }

  const blob = await response.blob();

  return {
    blob,
    contentType: response.headers.get("content-type") || blob.type || "",
    fileName:
      parseContentDispositionFileName(response.headers.get("content-disposition")) || "",
  };
}
